// Pure helper: przelicza rytm nauki (godziny, długość sesji, przerwy, pora
// zakończenia) oraz miękkie rekomendacje na podstawie preferencji z ankiety
// motywacyjnej i zachowania ucznia. Bez importów — testowalny i client-safe.
//
// Zasada: to ORIENTACYJNE sugestie, nie sztywne reguły ani diagnozy. Komunikaty
// są łagodne i nie zawierają twierdzeń medycznych.

export interface StudyWindow {
  start: string; // "HH:MM"
  end: string; // "HH:MM"
}

export interface FocusPreferences {
  focusDuration: number; // minuty, 0 = nie podano
  breakHabit: string; // regularly | irregularly | rarely | almost_never | reminders | no_auto | ""
  breakFrequency: number; // minuty, 0 = gdy poczuję zmęczenie / nie podano
  breakDuration: number; // minuty, 0 = nie podano
  automaticBreaksEnabled: boolean;
  latestStudyTime: string; // "HH:MM"
  studyWindows: StudyWindow[];
  studyTimes: string[]; // ["morning","afternoon","evening","night"]
}

// Presety czasu skupienia i przerwy (minuty) — używane w ankiecie i dialogu startu.
export const FOCUS_PRESETS = [25, 30, 45, 50, 60, 90];
export const BREAK_PRESETS = [5, 10, 15, 20, 30];
export const DEFAULT_FOCUS_MIN = 45;
export const DEFAULT_BREAK_MIN = 10;
export const PRE_BREAK_WINDOW_MIN = 5; // ile minut przed końcem sesji uprzedzić o przerwie

export const BREAK_HABIT_OPTIONS: { value: string; label: string }[] = [
  { value: "regularly", label: "Tak, regularnie" },
  { value: "irregularly", label: "Tak, ale nieregularnie" },
  { value: "rarely", label: "Rzadko" },
  { value: "almost_never", label: "Prawie nigdy" },
  { value: "reminders", label: "Chcę, żeby aplikacja przypominała mi o przerwach" },
  { value: "no_auto", label: "Nie chcę korzystać z automatycznych przerw" },
];

export const BREAK_FREQUENCY_OPTIONS: { value: number; label: string }[] = [
  { value: 25, label: "Co 20–30 min" },
  { value: 40, label: "Co 30–45 min" },
  { value: 55, label: "Co 45–60 min" },
  { value: 75, label: "Co 60–90 min" },
  { value: 0, label: "Dopiero kiedy poczuję zmęczenie" },
];

// ── Parsowanie / formatowanie godzin ──────────────────────────────
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

export function hhmmToMinutes(value: string): number | null {
  if (!HHMM.test(value)) return null;
  const [h, m] = value.split(":").map(Number);
  return h * 60 + m;
}

export function minutesToHhmm(total: number): string {
  const t = ((Math.round(total) % 1440) + 1440) % 1440;
  const h = Math.floor(t / 60);
  const m = t % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// ── Rozwiązywanie wartości z preferencji (z rozsądnymi domyślnymi) ─
export function resolveFocusMinutes(prefs: FocusPreferences | null): number {
  if (!prefs) return DEFAULT_FOCUS_MIN;
  if (prefs.focusDuration > 0) return clamp(prefs.focusDuration, 5, 180);
  if (prefs.breakFrequency > 0) return clamp(prefs.breakFrequency, 5, 180);
  return DEFAULT_FOCUS_MIN;
}

export function resolveBreakMinutes(prefs: FocusPreferences | null): number {
  if (!prefs) return DEFAULT_BREAK_MIN;
  if (prefs.breakDuration > 0) return clamp(prefs.breakDuration, 1, 60);
  return DEFAULT_BREAK_MIN;
}

export function autoBreaksEnabled(prefs: FocusPreferences | null): boolean {
  if (!prefs) return true;
  if (prefs.breakHabit === "no_auto") return false;
  return prefs.automaticBreaksEnabled;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

// Domyślne okna nauki wyprowadzone z grubych pór dnia (gdy uczeń nie podał
// dokładnych godzin, ale zaznaczył pory w ankiecie).
const BUCKET_WINDOWS: Record<string, StudyWindow> = {
  morning: { start: "08:00", end: "11:00" },
  afternoon: { start: "15:00", end: "18:00" },
  evening: { start: "18:00", end: "21:00" },
  night: { start: "21:00", end: "23:00" },
};

function windowsFromPrefs(prefs: FocusPreferences | null): StudyWindow[] {
  if (!prefs) return [];
  if (prefs.studyWindows && prefs.studyWindows.length > 0) {
    return [...prefs.studyWindows]
      .filter((w) => hhmmToMinutes(w.start) !== null && hhmmToMinutes(w.end) !== null)
      .sort((a, b) => (hhmmToMinutes(a.start)! - hhmmToMinutes(b.start)!));
  }
  // Z grubych pór dnia — w kolejności doby.
  const order = ["morning", "afternoon", "evening", "night"];
  return order.filter((b) => prefs.studyTimes.includes(b)).map((b) => BUCKET_WINDOWS[b]);
}

export type WindowState = "active" | "upcoming" | "past" | "none";

export interface DailyRhythm {
  bestWindow: StudyWindow | null;
  windowState: WindowState;
  sessionMin: number;
  breakMin: number;
  plannedEnd: string | null; // "HH:MM"
  autoBreaks: boolean;
  hasPlan: boolean; // czy uczeń podał godziny/porę zakończenia
}

/**
 * Zwraca rytm nauki na dany moment: najlepsze okno, proponowaną sesję,
 * przerwę i planowane zakończenie. Łączy dane z ankiety z aktualną godziną.
 */
export function getDailyRhythm(now: Date, prefs: FocusPreferences | null): DailyRhythm {
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const windows = windowsFromPrefs(prefs);
  const sessionMin = resolveFocusMinutes(prefs);
  const breakMin = resolveBreakMinutes(prefs);

  let bestWindow: StudyWindow | null = null;
  let windowState: WindowState = "none";

  const active = windows.find((w) => {
    const s = hhmmToMinutes(w.start)!;
    const e = hhmmToMinutes(w.end)!;
    return nowMin >= s && nowMin < e;
  });
  if (active) {
    bestWindow = active;
    windowState = "active";
  } else {
    const upcoming = windows.find((w) => hhmmToMinutes(w.start)! > nowMin);
    if (upcoming) {
      bestWindow = upcoming;
      windowState = "upcoming";
    } else if (windows.length > 0) {
      bestWindow = windows[windows.length - 1];
      windowState = "past";
    }
  }

  const latest = prefs?.latestStudyTime ? hhmmToMinutes(prefs.latestStudyTime) : null;
  let plannedEnd: string | null = prefs?.latestStudyTime && latest !== null ? prefs.latestStudyTime : null;
  if (!plannedEnd && bestWindow) plannedEnd = bestWindow.end;

  return {
    bestWindow,
    windowState,
    sessionMin,
    breakMin,
    plannedEnd,
    autoBreaks: autoBreaksEnabled(prefs),
    hasPlan: windows.length > 0 || latest !== null,
  };
}

export type DayTone = "good" | "winding" | "late" | "none";

export interface EndOfDayAnalysis {
  tone: DayTone;
  message: string;
}

/**
 * Miękka rekomendacja „do końca dnia". Bazuje na najpóźniejszej porze nauki
 * (lub końcu ostatniego okna) oraz proponowanej długości sesji. Nie zawiera
 * twierdzeń medycznych — to tylko orientacyjna wskazówka.
 */
export function getEndOfDayAnalysis(now: Date, prefs: FocusPreferences | null): EndOfDayAnalysis {
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const sessionMin = resolveFocusMinutes(prefs);
  const windows = windowsFromPrefs(prefs);

  let latest = prefs?.latestStudyTime ? hhmmToMinutes(prefs.latestStudyTime) : null;
  if (latest === null && windows.length > 0) {
    latest = Math.max(...windows.map((w) => hhmmToMinutes(w.end)!));
  }

  if (latest === null) {
    return {
      tone: "none",
      message: `Kiedy masz chwilę, dobrze wykorzystasz około ${sessionMin} min spokojnego skupienia.`,
    };
  }

  const remaining = latest - nowMin;

  if (remaining >= sessionMin) {
    return {
      tone: "good",
      message: `Masz jeszcze dobry moment na około ${sessionMin} min skupienia.`,
    };
  }
  if (remaining > 10) {
    return {
      tone: "winding",
      message: `Twoja zaplanowana nauka zbliża się do końca. Warto dziś zakończyć około ${minutesToHhmm(latest)}.`,
    };
  }
  if (remaining > -60) {
    return {
      tone: "late",
      message: "Zaplanowana nauka na dziś dobiega końca. Krótka powtórka w sam raz — trudniejsze tematy zostaw na jutro.",
    };
  }
  return {
    tone: "late",
    message: "To już późna pora. Odpocznij — świeża głowa jutro zrobi znacznie więcej.",
  };
}

export interface BehaviorSuggestion {
  suggestMin: number;
  rangeLabel: string; // np. „40–45 minut"
  message: string;
}

/**
 * Porównuje deklarowany (planowany) czas skupienia z rzeczywistym. Gdy uczeń
 * regularnie kończy wyraźnie wcześniej, proponuje krótszą sesję. Nigdy nie
 * zmienia ustawień sam — zwraca tylko sugestię do potwierdzenia.
 */
export function getBehaviorSuggestion(
  plannedAvgMin: number,
  actualAvgMin: number,
  samples: number
): BehaviorSuggestion | null {
  if (samples < 3) return null;
  if (plannedAvgMin <= 0 || actualAvgMin <= 0) return null;
  const diff = plannedAvgMin - actualAvgMin;
  // Sugeruj tylko przy wyraźnej i powtarzalnej różnicy.
  if (diff < 8 || actualAvgMin > plannedAvgMin * 0.8) return null;

  const suggestMin = Math.max(15, Math.round(actualAvgMin / 5) * 5);
  const low = Math.max(10, suggestMin - 5);
  const rangeLabel = `${low}–${suggestMin} minut`;
  return {
    suggestMin,
    rangeLabel,
    message: `Wygląda na to, że najlepiej idzie Ci skupienie przez około ${rangeLabel}. Chcesz ustawić taki czas?`,
  };
}

// Formatowanie czasu trwania w minutach na czytelny zapis (np. „1h 42 min").
export function formatDuration(ms: number): string {
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0 && m > 0) return `${h}h ${m} min`;
  if (h > 0) return `${h}h`;
  return `${m} min`;
}
